import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeNextPath } from '@affily/shared/redirect';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Seo } from '../../components/Seo.jsx';
import { Button, Alert } from '../../components/ui.jsx';

const copy = {
  creator: {
    title: 'Add a creator profile',
    body: 'Publish your rates and audience stats, apply to programs, and get paid through the same account you already have.',
    next: '/dashboard/creator/profile',
  },
  brand: {
    title: 'Add a brand profile',
    body: 'List programs with public terms and invite creators, from the account you already have.',
    next: '/dashboard/brand/profile',
  },
};

/**
 * Adding the second role.
 *
 * One account can hold both. A second signup would fragment someone's earnings
 * history across two logins for no reason.
 */
export default function AddRole() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { addRole, user } = useAuth();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const role = params.get('role') === 'brand' ? 'brand' : 'creator';
  const c = copy[role];
  const alreadyHas = user?.roles?.includes(role);
  // A caller can ask to land somewhere specific once the role is added, but
  // only on this site.
  const destination = safeNextPath(params.get('next')) ?? c.next;

  async function add() {
    setBusy(true);
    setError('');
    try {
      await addRole(role);
      navigate(destination);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title={c.title} noindex />
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <h1 className="text-3xl font-semibold tracking-tight">{c.title}</h1>
        <p className="mt-3 text-ink-muted">{c.body}</p>
        <p className="mt-3 text-sm text-ink-faint">
          Same login, same email. You can switch sides from the dashboard header.
        </p>

        <Alert className="mt-4">{error}</Alert>

        <div className="mt-6 flex gap-3">
          {alreadyHas ? (
            <Button to={destination} size="lg">
              Continue
            </Button>
          ) : (
            <Button onClick={add} size="lg" disabled={busy}>
              {busy ? 'Adding…' : `Add ${role} profile`}
            </Button>
          )}
          <Button to="/dashboard" variant="ghost" size="lg">
            Not now
          </Button>
        </div>
      </div>
    </>
  );
}
